import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../config/axios';
import { ProcessedEmail, EmailStats } from '../../types/emailAccount';

interface MyEmailsState {
  emails: ProcessedEmail[];
  currentEmail: ProcessedEmail | null;
  stats: EmailStats | null;
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
}

const initialState: MyEmailsState = {
  emails: [],
  currentEmail: null,
  stats: null,
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
};

// Async thunks
export const fetchMyEmails = createAsyncThunk(
  'myEmails/fetchAll',
  async ({ page = 1, limit = 50, status }: { page?: number; limit?: number; status?: string }, { rejectWithValue }) => {
    try {
      const params: any = { page, limit };
      if (status) params.status = status;
      
      const response = await axios.get('/user/my-emails', { params });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch emails');
    }
  }
);

export const fetchEmailDetails = createAsyncThunk(
  'myEmails/fetchDetails',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/user/my-emails/${id}`);
      return response.data.email;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch email details');
    }
  }
);

export const fetchMyEmailStats = createAsyncThunk(
  'myEmails/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/user/my-emails-stats');
      return response.data.stats;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch email stats');
    }
  }
);

// Slice
const myEmailsSlice = createSlice({
  name: 'myEmails',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentEmail: (state) => {
      state.currentEmail = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch emails
    builder.addCase(fetchMyEmails.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMyEmails.fulfilled, (state, action) => {
      state.loading = false;
      state.emails = action.payload.emails;
      state.currentPage = action.payload.pagination.page;
      state.totalPages = Math.ceil(action.payload.pagination.total / action.payload.pagination.limit);
    });
    builder.addCase(fetchMyEmails.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch email details
    builder.addCase(fetchEmailDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchEmailDetails.fulfilled, (state, action: PayloadAction<ProcessedEmail>) => {
      state.loading = false;
      state.currentEmail = action.payload;
    });
    builder.addCase(fetchEmailDetails.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch stats
    builder.addCase(fetchMyEmailStats.fulfilled, (state, action: PayloadAction<EmailStats>) => {
      state.stats = action.payload;
    });
  },
});

export const { clearError, clearCurrentEmail } = myEmailsSlice.actions;
export default myEmailsSlice.reducer;

